import {
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  DialogTitle,
  IconButton,
  Snackbar,
  Grid,
  Alert,
  Box,
  InputAdornment,
  Divider,
  Tooltip,
  Autocomplete,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import Icon from "@mdi/react";
import {
  mdiAlertCircleOutline,
  mdiArrowLeft,
  mdiBarcodeScan,
  mdiCheck,
  mdiChevronDown,
  mdiClose,
  mdiPlus,
} from "@mdi/js";

import { IAddCountDialogProps, ICountEntry, IUpdateReconcileDetailsRequest } from "../../../../interfaces";
import { ConfirmationDialog, DialogStyle, Loader } from "../../../../components";
import styles from "./addCountDialog.module.scss";
import TagService from "../../../../api/services/TagService";
import CountEntryTable from "./CountEntryTable";
import { useClient } from "../../../../utils/ClientContext";
import { containerizedDetailService, inventoryService, tagService } from "../../../../api/services";
import { BarcodeScanner, BarcodeStringFormat } from '../../../../wrapper/react-qr-barcode-scanner';
import { InventoryExceptionType } from "../../../../utils/Enum/InventoryExceptionType";
import { validateLocally } from "../../../../utils";

const AddCountDialog: React.FC<IAddCountDialogProps> = ({
  open,
  onClose,
  onSubmit,
  isPalletized,
  isCartoned,
  isSerialized,
  isLotted,
  location,
  item,
  tagId,
  locationId,
  uomId,
  tagName,
  itemId,
  isAdmin,
  isCounter,
  isEdit,
  actionEditIndex,
  isAllowSubmit,
  isEditOnContainerId
}) => {
  //#region Required
  const initialForm = {
    pallet: "",
    carton: "",
    lot: "",
    serialNo: "",
    qty: isSerialized ? "1" : "",
  };

  const [formValues, setFormValues] = useState({ ...initialForm });
  const [errors, setErrors] = useState<
    Record<keyof typeof formValues, boolean>
  >({
    pallet: false,
    carton: false,
    lot: false,
    serialNo: false,
    qty: false,
  });

  const [entries, setEntries] = useState<ICountEntry[]>([]);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOnceEdited, setIsOnceEdited] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [severity, setSeverity] = useState<"success" | "error">("success");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const tableRef = useRef<HTMLTableSectionElement>(null);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [dialogMessage, setDialogMessage] = useState("");
  const [dialogTitle, setDialogTitle] = useState("");
  const [inactiveDialogOpen, setInactiveDialogOpen] = useState(false);
  const [smallestEntity, setSmallestEntity] = useState("");
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const [uoms, setUoms] = useState<{ id: number; name: string }[]>([]);
  const [selectedUomId, setSelectedUomId] = useState<number | null>(null);
  const inventoryId = Number(localStorage.getItem("inventoryId"));
  const { client } = useClient();
  const clientId = client.id;
  const userId = Number(localStorage.getItem("userId"));
  const [showLoader, setShowLoader] = useState(false);
  const [scanningField, setScanningField] = useState<
    keyof typeof formValues | null
  >(null);
  const [showScanner, setShowScanner] = useState(false);
  const [fieldOrder, setFieldOrder] = useState<(keyof typeof formValues)[]>([]);
  const [isSubmitWithScanner, setIsSubmitWithScanner] = useState(false);
  const [isUomInitialized, setIsUomInitialized] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
    const [isUomDisabled,setIsUomDisabled]= useState(false);
  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };
  const serialNoField = "serialNo";
  const lotField = "lot";
  const cartonField = "carton";
  const palletField = "pallet";
  const qtyField = "qty";
  const resetForm = () => {
    setFormValues({
      pallet: "",
      carton: "",
      lot: "",
      serialNo: "",
      qty: isSerialized ? "1" : "",
    });
    setErrors({
      pallet: false,
      carton: false,
      lot: false,
      serialNo: false,
      qty: false,
    });
    setEditIndex(null);
    setIsSubmitting(false);
  };
  const [baseFlags, setBaseFlags] = useState<Map<number, any>>(new Map());
  const fetchContainerizedDetail = async () => {
    if (tagId) {
      const payload = {
        tagId: +tagId,
        isOnlyException: false,
      };
      const response =
        await containerizedDetailService.getTagContainerizedDetailAsync(
          payload
        );
      if (response.data.lstContainerizedDetail) {
        const fetchedEntries = response.data.lstContainerizedDetail.map(
          (entry: ICountEntry) => ({
            ...entry,
            localExceptions: entry.localExceptions || null,
            exceptions: entry.otherTagExceptions || null
          })
        );
        const flags = new Map();
        fetchedEntries.forEach((e: any) => {
          flags.set(e.id ?? Math.random(), {
            hasUnacceptedSerialException: e.hasUnacceptedSerialException,
            hasUnacceptedCartonException: e.hasUnacceptedCartonException,
            hasUnacceptedPalletException: e.hasUnacceptedPalletException,
          });
        });

        setBaseFlags(flags);
        setEntries(fetchedEntries);

        fetchUOMs(response.data.itemId, response.data.uomId);
      }
    }
  };
  useEffect(() => {
    resetForm();
    const determineSmallestEntity = () => {
      if (isSerialized) return serialNoField;
      if (isLotted) return lotField;
      if (isCartoned) return cartonField;
      return palletField;
    };
    setSmallestEntity(determineSmallestEntity());
    if (tagId && tagId > 0) {
      fetchContainerizedDetail();
    }
    const currFieldOrder: (keyof typeof formValues)[] = [];
    if (isPalletized) currFieldOrder.push(palletField);
    if (isCartoned) currFieldOrder.push(cartonField);
    if (isLotted) currFieldOrder.push(lotField);
    if (isSerialized) currFieldOrder.push(serialNoField);
    if (!isSerialized) currFieldOrder.push(qtyField);
    setFieldOrder(currFieldOrder);
  }, [isPalletized, isCartoned, isSerialized, isLotted, location, item, tagId]);
  const resetPartialForm = () => {
    setFormValues({
      pallet: isCartoned || isLotted || isSerialized ? formValues.pallet : "",
      carton: isLotted || isSerialized ? formValues.carton : "",
      lot: isSerialized ? formValues.lot : "",
      serialNo: "",
      qty: isSerialized ? "1" : ""
    });
    setErrors({
      pallet: false,
      carton: false,
      lot: false,
      serialNo: false,
      qty: false,
    });
    setEditIndex(null);
    setIsSubmitting(false);
  };
  useEffect(() => {
    if (
      actionEditIndex != null &&
      tagId &&
      !isOnceEdited &&
      entries?.length > 0
    ) {
      setIsOnceEdited(true);
       let indexToEdit = actionEditIndex;

    if (isEditOnContainerId) {
      const foundIndex = entries.findIndex(
        (entry) => entry.id === indexToEdit
      );
      if (foundIndex !== -1) {
        indexToEdit = foundIndex;
      }
    }

    handleEdit(indexToEdit);
    }
  }, [entries]);
  useEffect(() => {
    if (isSubmitWithScanner) {
      setIsSubmitWithScanner(false);
      handleAddOrUpdate();
    }
  }, [isSubmitWithScanner]);
  const handleClose = () => {
    if (editIndex !== null) {
      resetForm();
    } else {
      resetForm();
      setEntries([]);
    }
    onClose?.();
  };
  const playBeep = () => {
    const audio = new Audio("/beep.mp3");
    audio.play().catch((err) => console.warn("Beep failed:", err));
  };
  useEffect(() => {
    const checkValidity = () => {
      const isValid = validateForm(true);
      setIsFormValid(isValid);
    };

    checkValidity();
  }, [formValues, isSerialized, isLotted, isCartoned, isPalletized]); // include all flags affecting validation

  const handleSnackbarClose = () => setOpenSnackbar(false);
  const shouldDisableSubmit =
    isSubmitting ||
    !isAllowSubmit ||
    ((isEdit || (tagId ?? 0) > 0) && !selectedUomId) ||
    entries?.length < 1;
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const updated = { ...formValues, [name]: value };

    if (isSerialized && name === serialNoField) {
      updated.qty = "1";
    }

    setFormValues(updated);

    let hasError = false;

    if (name === qtyField) {
      hasError = !value || isNaN(Number(value));
    } else if ([palletField, cartonField].includes(name)) {
      hasError = value !== "";
    } else {
      hasError = !value;
    }

    setErrors((prev) => ({ ...prev, [name]: hasError }));
  };
  const isValidQty = (qty: unknown): boolean => {
    const strQty = String(qty).trim();
    const num = Number(strQty);
    return strQty !== "" && !isNaN(num) && num >= 0;
  };

  const validateForm = (onlyValidate: boolean = false) => {
    const validation: Record<keyof typeof formValues, boolean> = {
      pallet: false,
      carton: false,
      lot: isLotted && formValues.lot.trim() === "",
      serialNo: isSerialized && formValues.serialNo.trim() === "",
      qty: !isValidQty(formValues.qty),
    };
    if (!onlyValidate) {
      setErrors(validation);
    }
    return !Object.values(validation).some(Boolean);
  };
  const revalidateAllExceptions = (currentEntries: ICountEntry[]) => {
    const { updatedEntries } = validateLocally({
  entries,
  newEntry: {} as any,
  editedIndex: null,
  baseFlags,
  isSerialized,
  isCartoned,
  isPalletized,
});
return updatedEntries;
  };
  const handleEdit = (index: number) => {
    const entry = entries[index];
    setFormValues(entry);
    setEditIndex(index);

    // Recalculate error state based on the values being set
    const updatedErrors: typeof errors = {
      pallet: false,
      carton: false,
      lot: isLotted && !entry.lot?.trim(),
      serialNo: isSerialized && !entry.serialNo?.trim(),
      qty: !isValidQty(entry.qty),
    };
    setErrors(updatedErrors);
  };

  const handleDelete = (index: number) => {
    setDeleteIndex(index);
    setDialogTitle("Delete");
    setDialogMessage("Are you sure you want to delete this record?");
    setInactiveDialogOpen(true);
  };
  const onDialogClose = () => {
    setDeleteIndex(null);
    setInactiveDialogOpen(false);
    setDialogMessage("");
    setDialogTitle("");
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    if (
      entries.length === 0 ||
      (!isCounter && !isAdmin) ||
      (isEdit && tagId === 0) ||
      !isAllowSubmit
    ) {
      if (entries.length === 0) {
        setSnackbarMessage("Please add at least one entry.");
      } else {
        setSnackbarMessage("Something went wrong!");
      }
      setSeverity("error");
      setOpenSnackbar(true);
      setIsSubmitting(false);
      return;
    }

    try {
      if (onSubmit) {
        setShowLoader(true);
        if (isCounter) {
          if (isEdit) {
            let details = await updateTageCounter();
            onSubmit(details);
          } else {
            await addTagCounter();
            onSubmit();
          }
        } else if (isAdmin) {
          updateReconcileDetails();
          onSubmit();
        }
      }
      setShowLoader(false);
      onClose();
    } catch {
      setSnackbarMessage("Error occurred while adding tags.");
      setSeverity("error");
      setOpenSnackbar(true);
    }
  };
  const getTotalCount = () => {
    return parseFloat(
      entries
        .reduce((total: number, e: any) => total + parseFloat(e.qty || "0"), 0)
        .toString()
    );
  };
  const addTagCounter = async () => {
    setIsSubmitting(true);
    const tagData = {
      TagName: tagName,
      LocationId: locationId ? locationId : 0,
      ClientId: clientId,
      InventoryId: inventoryId,
      ItemId: itemId ? itemId : 0,
      QtyCount: getTotalCount(),
      CountUomid: uomId ? uomId : null,
      CreatedBy: userId,
      isPalletized: isPalletized,
      isCartoned: isCartoned,
      isSerialized: isSerialized,
      isLotted: isLotted,
      ContainerizedDetail: entries,
    };
    try {
      await tagService.addTagAsync(tagData);
      submitSuccess("Tag added successfully.");
    } catch (error: any) {
      submitError(error, "Error occurred while adding tags.");
    }
  };
  const updateReconcileDetails = async () => {
    if (tagId != null) {
      const request: IUpdateReconcileDetailsRequest = {
        tagId: tagId,
        reconcileQty: getTotalCount(),
        reconcilerUserId: userId || 0,
        reconcileUOMId: selectedUomId || 0,
        clientId: clientId,
        isPalletized: isPalletized,
        isCartoned: isCartoned,
        isSerialized: isSerialized,
        isLotted: isLotted,
        ContainerizedDetail: entries,
      };
      try {
        const response = await TagService.updateReconcileDetailsRequest(
          request
        );
        if (response.data && response.data.isSuccess) {
          submitSuccess("Updated successfully!");
        } else {
          setSnackbarMessage("Failed to update reconciliation tag details.");
          setSeverity("error");
        }
      } catch (error: any) {
        submitError(error, "Failed to update reconciliation tag details.");
      }
    }
  };
  //Update Tag details on submit
  const updateTageCounter = async () => {
    const updatetagData = {
      UserId: userId,
      ClientId: clientId,
      TagId: tagId,
      Quantity: getTotalCount(),
      UOMId: selectedUomId ? selectedUomId : null,
      isCounter: isCounter,
      ContainerizedDetail: entries,
      isSerialized: isSerialized,
      isLotted: isLotted,
      isCartoned: isCartoned,
      isPalletized: isPalletized,
    };
    try {
      const currUom = uoms.find((uom) => uom.id === selectedUomId) || null;
      const updatedDetails = {
        uomDisplay: currUom ? currUom.name : "",
        uomId: currUom ? currUom.id : 0,
        tagQtyCount: getTotalCount(),
      };
      await TagService.updateTagAsync(updatetagData);
      submitSuccess("Tag updated successfully.");
      return updatedDetails;
    } catch (error: any) {
      submitError(error, "Error occurred while updating tags.");
    }
    return null;
  };

  const submitError = (error: any, message: string) => {
    setIsSubmitting(false);
    const errorMessages = error.response?.data?.errors || [message];
    setSeverity("error");
    setSnackbarMessage(errorMessages.join(", "));
    setOpenSnackbar(true);
    console.error(error);
  };
  const submitSuccess = (message: string) => {
    setSnackbarMessage(message);
    setSeverity("success");
    setOpenSnackbar(true);
    setTimeout(() => {
      handleCloseSnackbar();
    }, 1000);
  };
  const getHelperText = (
    name: keyof typeof formValues,
    label: string,
    isNumber: boolean
  ): string => {
    if (!errors[name]) return "";

    if (isNumber) return "Qty must be a valid number and cannot be negative";
    return `${label} is required`;
  };
  const [palletNumbers, setPalletNumbers] = useState<string[]>([]);
  const [cartonNumbers, setCartonNumbers] = useState<string[]>([]);
  const [lotIds, setLotIds] = useState<string[]>([]);
  const [allPalletNos, setAllPalletNos] = useState<string[]>([]);
  const [allCartonNos, setAllCartonNos] = useState<string[]>([]);
  const [allLotIds, setAllLotIds] = useState<string[]>([]);
  useEffect(() => {
    const fetchTrackingInfo = async () => {
      try {
        const inventoryId = Number(localStorage.getItem("inventoryId"));
        if (!inventoryId) {
          console.error("Invalid or missing inventoryId in localStorage.");
        }
        const response =
          await containerizedDetailService.getTrackingInfoByInventoryRequest(
            inventoryId
          );
        if (response.data) {
          setPalletNumbers(response.data.palletNumbers || []);
          setCartonNumbers(response.data.cartonNumbers || []);
          setLotIds(response.data.lotIds || []);
        }
      } catch (error) {
        setSeverity("error");
        setSnackbarMessage("Failed to fetch tracking info.");
        setOpenSnackbar(true);
        console.error("Failed to fetch tracking info:", error);
      }
    };

    fetchTrackingInfo();
  }, []);

  useEffect(() => {
    const palletList = entries.map((e) => e.pallet).filter(Boolean);
    const cartonList = entries.map((e) => e.carton).filter(Boolean);
    const lotList = entries.map((e) => e.lot).filter(Boolean);
    setAllPalletNos(Array.from(new Set([...palletNumbers, ...palletList])));
    setAllCartonNos(Array.from(new Set([...cartonNumbers, ...cartonList])));
    setAllLotIds(Array.from(new Set([...lotIds, ...lotList])));
  }, [entries, palletNumbers, cartonNumbers, lotIds]);
  const [suggestionState, setSuggestionState] = useState<{
    [key: string]: {
      filteredOptions: string[];
      show: boolean;
    };
  }>({});
  const fetchUOMs = async (currItem: number, currUOM: number) => {
  if (!currItem) return;
  try {
    const response = await tagService.getItemUOMAsync(currItem);
    const fetchedUoms = response.data.unitOfMeasures || [];
    setUoms(fetchedUoms);
    if (currUOM) {
      setSelectedUomId(currUOM);
    } else if (fetchedUoms.length === 1) {
      setSelectedUomId(fetchedUoms[0].id);
    } else {
      const primary = fetchedUoms.find((u: any) => u.isPrimary);
      if (primary) {
        setSelectedUomId(primary.id);
      }
    }

    setIsUomDisabled(isSerialized);

    setIsUomInitialized(true);
  } catch (err) {
    console.error("Error fetching UOMs:", err);
  }
};

  const renderTextField = (
    name: keyof typeof formValues,
    label: string,
    isNumber = false,
    visible: boolean,
    isRequired: boolean,
    isScan: boolean,
    isAddNewButton: boolean,
    isDropDown: boolean,
    dropdownOptions: string[] = []
  ) => {
    if (!visible) return null;
    const handleClearField = () => {
      setFormValues((prev) => ({
        ...prev,
        [name]: name === qtyField && isSerialized ? "1" : "",
      }));
      setErrors((prev) => ({
        ...prev,
        [name]: false,
      }));
      setTimeout(() => {
        inputRefs.current[name]?.focus();
      }, 0);
    };

    const updateSuggestions = (
      fieldName: string,
      value: string,
      options: string[]
    ) => {
      const filtered = options.filter((option) =>
        option.toLowerCase().includes(value.toLowerCase())
      );
      setSuggestionState((prev) => ({
        ...prev,
        [fieldName]: {
          filteredOptions: filtered,
          show: true,
        },
      }));
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;

      if (isNumber) {
        // Allow only numbers with up to 2 decimal places
        const numberRegex = /^\d{0,8}(\.\d{0,2})?$/;
        if (!numberRegex.test(value) && value !== "") return;
      }

      setFormValues((prev) => ({
        ...prev,
        [name]: value,
      }));
      setErrors((prev) => ({
        ...prev,
        [name]: false,
      }));

      if (isDropDown) {
        updateSuggestions(name, value, dropdownOptions);
      }
    };

    const handleSuggestionClick = (suggestion: string) => {
      setFormValues((prev) => ({
        ...prev,
        [name]: suggestion,
      }));
      setErrors((prev) => ({
        ...prev,
        [name]: false,
      }));
      setSuggestionState((prev) => ({
        ...prev,
        [name]: {
          ...prev[name],
          show: false,
        },
      }));
    };

    return (
      <Grid size={{ xs: 12 }} style={{ position: "relative" }}>
        <FormControl fullWidth>
          <TextField
            inputRef={(el) => (inputRefs.current[name] = el)}
            fullWidth
            required={isRequired}
            name={name}
            id={name}
            autoComplete="off"
            value={formValues[name]}
            label={label}
            placeholder={`Enter ${label}`}
            onChange={handleInputChange}
            inputProps={{
              inputMode: isNumber ? "decimal" : "text",
              pattern: isNumber ? "^(0|[1-9]\\d*)(\\.\\d{1,2})?$" : undefined,
              readOnly: name === qtyField && isSerialized,
              maxLength: ["serialNo", "lot", "carton", "pallet"].includes(name)
                ? 50
                : undefined,
            }}
            error={errors[name]}
            helperText={getHelperText(name, label, isNumber)}
            InputProps={{
              endAdornment:
                isScan || isAddNewButton ? (
                  <InputAdornment position="end">
                    {isScan && (
                      <IconButton
                        onClick={() => {
                          setScanningField(name);
                          setShowScanner(true);
                        }}
                        tabIndex={-1}
                      >
                        <Icon path={mdiBarcodeScan} size={0.8} />
                      </IconButton>
                    )}
                    {isAddNewButton && (
                      <Tooltip title={"Change " + label}>
                        <span>
                          <IconButton onClick={handleClearField} tabIndex={-1}>
                            <Icon path={mdiPlus} size={0.8} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </InputAdornment>
                ) : null,
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleAddOrUpdate();
                setSuggestionState((prev) => ({
                  ...prev,
                  [name]: {
                    ...prev[name],
                    show: false,
                  },
                }));
              }
            }}
            onBlur={() => {
              setFormValues((prev) => ({
                ...prev,
                [name]: String(prev[name] ?? "").trim(),
              }));
              setTimeout(() => {
                setSuggestionState((prev) => ({
                  ...prev,
                  [name]: {
                    ...prev[name],
                    show: false,
                  },
                }));
              }, 200);
            }}
            onFocus={() => {
              if (isDropDown && formValues[name]) {
                updateSuggestions(name, formValues[name], dropdownOptions);
              }
            }}
          />
          {isDropDown &&
            suggestionState[name]?.show &&
            suggestionState[name]?.filteredOptions.length > 0 && (
              <ul
                style={{
                  listStyle: "none",
                  margin: 0,
                  padding: "0px",
                  border: "1px solid #ccc",
                  borderTop: "none",
                  maxHeight: "150px",
                  overflowY: "auto",
                  backgroundColor: "white",
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  zIndex: 10,
                  borderBottomLeftRadius: 5,
                  borderBottomRightRadius: 5,
                }}
              >
                {suggestionState[name].filteredOptions.map((option, idx) => (
                  <li
                    key={idx}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleSuggestionClick(option)}
                    style={{
                      padding: "6px 12px",
                      cursor: "pointer",
                      backgroundColor: "white",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#f0f0f0";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "white";
                    }}
                  >
                    {option}
                  </li>
                ))}
              </ul>
            )}
        </FormControl>
      </Grid>
    );
  };
  const getIcon = () =>
    severity === "success" ? (
      <Icon path={mdiCheck} size={1} />
    ) : (
      <Icon path={mdiAlertCircleOutline} size={1} />
    );
  //#endregion
  const handleAddOrUpdate = async () => {
    if (!validateForm()) return;
    const inventoryId = Number(localStorage.getItem("inventoryId"));
    setIsOnceEdited(true);
    let indexToScroll = entries.length;
    let localEditIndex = editIndex;
    let localFormValues: ICountEntry = { ...formValues };

    let matchIndex: number | null = null;
    const newLocalExceptions: number[] = [];
    const newApiExceptions: number[] = [];

    const normalize = (value?: string) => value?.trim().toLowerCase() || "";
    const serial = normalize(localFormValues.serialNo);
    const carton = normalize(localFormValues.carton);
    const pallet = normalize(localFormValues.pallet);
    let isMerge = false;
        // ---------- Quantity Merge Logic ----------
    if (!isSerialized) {
    const matchIndex = entries.findIndex(
      (entry, i) =>
        (localEditIndex === null || localEditIndex !== i) &&
        (!isLotted ||
          normalize(entry.lot) === normalize(localFormValues.lot)) &&
        (!isPalletized ||
          normalize(entry.pallet) === normalize(localFormValues.pallet)) &&
        (!isCartoned ||
          normalize(entry.carton) === normalize(localFormValues.carton))
    );

    if (matchIndex !== -1) {
      const totalQty =
        Number(entries[matchIndex].qty) + Number(localFormValues.qty);
      const mergedQty =
        totalQty % 1 !== 0 ? totalQty.toFixed(2) : totalQty.toString();

      // Update the existing entry qty directly
      entries[matchIndex] = {
        ...entries[matchIndex],
        qty: mergedQty,
      };

      localEditIndex = matchIndex;
      isMerge = true;
      if(editIndex != null){
        await handleOnConfirm(editIndex, localEditIndex);
        return;
      }
    }
  }
    
const apiResponse = await containerizedDetailService.validateContainerizedDetail({
  inventoryId,
  locationId,
  serialNo: isSerialized ? serial : null,
  carton: isCartoned ? carton : null,
  pallet: isPalletized ? pallet : null,
  tagId: tagId ?? null,
  itemId,
});

const exceptionString = apiResponse?.data?.exception ?? "";
localFormValues.otherTagExceptions = exceptionString;

const { updatedEntries, updatedEntry } = validateLocally({
  entries,
  newEntry: localFormValues,
  editedIndex: isMerge ? null : localEditIndex, // skip inserting when merging
  baseFlags,
  isSerialized,
  isCartoned,
  isPalletized,
  skipInsert: isMerge, // IMPORTANT: don't insert if merging
});
setEntries(updatedEntries);
  if (updatedEntry?.localExceptions?.trim() || apiResponse?.data?.exception?.trim()) {
    setSnackbarMessage("System noticed an exception.");
    setSeverity("error");
    setOpenSnackbar(true);
    setTimeout(() => {
      setOpenSnackbar(false);
    }, 2000);
  }
    resetPartialForm();
    setTimeout(() => {
      const tbody = tableRef.current;
      if (tbody) {
        const row = tbody.children[indexToScroll] as HTMLElement;
        if (row) row.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      try {
        document.getElementById(smallestEntity)?.focus();
      } catch {
        // Safe fallback
      }
    }, 100);
  };
const handleOnConfirm = async (manualDeleteIndex?:number | null, editIndexAfterDelete?:number | null) => {
  let deleteI = deleteIndex;
  if(manualDeleteIndex !== undefined && manualDeleteIndex !== null){
    deleteI = manualDeleteIndex;
  }
  if (deleteI == null) return;

  const updated = [...entries];
  updated.splice(deleteI, 1);

  const { updatedEntries } = validateLocally({
    entries: updated,
    newEntry: updated[0] ?? {}, // provide one valid dummy entry (won't be used)
    editedIndex: null,
    baseFlags,
    isSerialized,
    isCartoned,
    isPalletized,
    skipInsert: true,
  });
  setEntries(updatedEntries);
  setDeleteIndex(null);

  if (editIndex === deleteI) {
    resetForm();
    if(editIndexAfterDelete !== undefined && editIndexAfterDelete !== null){
      await handleEdit(editIndexAfterDelete);
      resetPartialForm();
    }
  }
};


  return (
    <DialogStyle open={open}>
      <DialogTitle className={styles.countTitle}>
        {tagId ? "Edit Tag" : "Add Tag"}
      </DialogTitle>
      <IconButton
        onClick={handleClose}
        className={styles.countCloseButton}
        tabIndex={-1}
      >
        <Icon path={mdiClose} size={1} />
      </IconButton>
      <DialogContent>
        <div className={styles.header}>
          <Tooltip title="Back">
            <span>
              <IconButton
                onClick={handleClose}
                sx={{ height: "24px", width: "24px" }}
                className={styles.countBackButton}
              >
                <Icon path={mdiArrowLeft} size={1} />
              </IconButton>
            </span>
          </Tooltip>
          <label className={styles.itemNameLabel}>{item}</label>
        </div>
        <Divider sx={{ borderColor: "#D9DDE8", my: 1 }} />
        {isEdit && (
          <Grid>
            <Autocomplete
              id="uom"
              popupIcon={<Icon path={mdiChevronDown} size={1} />}
              options={uoms}
              value={uoms.find((uom) => uom.id === selectedUomId) || null}
              getOptionLabel={(option) =>
                typeof option === "string" ? option : option.name || ""
              }
              onChange={(event, newValue) => {
                setSelectedUomId(newValue?.id || null);
              }}
              disabled={isUomDisabled} 
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  label="UOM"
                  name="uom"
                  error={
                    isUomInitialized &&
                    (isEdit || (tagId ?? 0) > 0) &&
                    !selectedUomId
                  }
                  helperText={
                    isUomInitialized &&
                    (isEdit || (tagId ?? 0) > 0) &&
                    !selectedUomId
                      ? "UOM is required"
                      : ""
                  }
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    ...params.inputProps,
                    placeholder: "Select UOM",
                  }}
                />
              )}
            />
          </Grid>
        )}
        {isEdit && <Divider sx={{ borderColor: "#D9DDE8", my: 1 }} />}
        <Grid container spacing={1}>
          {renderTextField(
            palletField,
            "Pallet No.",
            false,
            isPalletized,
            false,
            true,
            isCartoned || isLotted || isSerialized ? true : false,
            true,
            allPalletNos
          )}
          {renderTextField(
            cartonField,
            "Carton No.",
            false,
            isCartoned,
            false,
            true,
            isLotted || isSerialized ? true : false,
            true,
            allCartonNos
          )}
          {renderTextField(
            lotField,
            "Lot ID",
            false,
            isLotted,
            true,
            true,
            isSerialized,
            true,
            allLotIds
          )}
          {renderTextField(
            serialNoField,
            "Serial No.",
            false,
            isSerialized,
            true,
            true,
            false,
            false
          )}
          {renderTextField(
            qtyField,
            "Quantity",
            true,
            !isSerialized,
            true,
            false,
            false,
            false
          )}
          <Grid size={{ xs: 12 }}>
            <div className={styles.addButtonContainer}>
              {/* <Button fullWidth variant="contained" onClick={handleAddOrUpdate}>
              {editIndex !== null ? "Update" : "Add"}
            </Button> */}
              <Button
                variant="outlined"
                onClick={resetForm}
                sx={{ display: editIndex !== null ? "block" : "none" }}
              >
                {editIndex !== null ? "Cancel" : "Clear"}
              </Button>
              &nbsp;&nbsp;
              <Button
                variant="contained"
                onClick={handleAddOrUpdate}
                disabled={!isFormValid}
              >
                {editIndex !== null ? "Save" : "Add"}
              </Button>
            </div>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Box className={styles.finalTagQtyBox}>
              Final Tag Quantity:
              <span style={{ marginLeft: "8px", color: "#1976d2" }}>
                {entries.reduce(
                  (total, e) => total + parseFloat(e.qty || "0"),
                  0
                )}
              </span>
            </Box>
          </Grid>

          {entries.length > 0 && (
            <Grid size={{ xs: 12 }}>
              <CountEntryTable
                entries={entries}
                isPalletized={isPalletized}
                isCartoned={isCartoned}
                isLotted={isLotted}
                isSerialized={isSerialized}
                onEdit={handleEdit}
                onDelete={handleDelete}
                isDeletable={true}
                isAllowSubmit={isAllowSubmit}
                isCounter={isCounter}
                isOnClickResolveException={false}
                isLocked={false}
              />
            </Grid>
          )}
          {showLoader && <Loader />}
        </Grid>
      </DialogContent>

      <DialogActions className={styles.countActions}>
        {/* <Button fullWidth variant="outlined" onClick={handleClose}>
          <Icon path={mdiArrowLeft} size={1} />
        </Button> */}
        <Button
          fullWidth
          variant="contained"
          onClick={handleSubmit}
          tabIndex={-1}
          disabled={shouldDisableSubmit}
        >
          Submit
        </Button>
      </DialogActions>

      <ConfirmationDialog
        open={inactiveDialogOpen}
        onClose={() => onDialogClose()}
        onConfirm={() => handleOnConfirm()}
        message={dialogMessage}
        title={dialogTitle}
      />
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={openSnackbar}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
      >
        <Alert
          severity={severity}
          onClose={handleSnackbarClose}
          icon={getIcon()}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      {showScanner && scanningField && (
        <div className={styles.scannerOverlay}>
          <BarcodeScanner
            width={500}
            height={500}
            onUpdate={(err: any, result: any) => {
              if (result) {
                playBeep();
                setFormValues((prev) => ({
                  ...prev,
                  [scanningField]: result.text,
                }));
                setShowScanner(false);
                setScanningField(null);
                const currentField = scanningField;
                setScanningField(null);
                // Refocus to the input
                setTimeout(() => {
                  const currentIndex = fieldOrder.indexOf(
                    currentField as keyof typeof formValues
                  );
                  const isLastField = currentIndex === fieldOrder.length - 1;
                  if (!isLastField) {
                    const nextField = fieldOrder[currentIndex + 1];
                    inputRefs.current[nextField]?.focus();
                  } else if (result.text?.trim()) {
                    setIsSubmitWithScanner(true);
                  }
                }, 100);
              }
            }}
          />
          <Button
            sx={{ margin: 0.1 }}
            onClick={() => {
              setShowScanner(false);
              setScanningField(null);
            }}
          >
            Cancel
          </Button>
        </div>
      )}
    </DialogStyle>
  );
};

export default AddCountDialog;
